import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { Alert, Button, Checkbox, Form, Label, Select } from '@/shared/components';
import { PermissionGuard, usePermissions } from '@/features/auth';
import { isApiError, isNotFoundError } from '@/services/api';
import type { Product } from '@/features/products/types/products.types';
import { resolveProductCampaign } from '../services/campaign';
import { useCampaignTransferRules } from '../hooks/useCampaignTransferRules';
import { toTransferRuleFormValues, toTransferRulePayload } from '../services/transferRule';
import { transferRuleSchema, TRANSFER_POLICY_VALUES } from '../schemas/transferRule.schema';
import type { TransferRuleFormValues } from '../schemas/transferRule.schema';
import { ForbiddenDirectionsEditor } from './ForbiddenDirectionsEditor';

interface Props {
    productId: string;
    product: Product;
}

const resolveCampaignKeys = {
    forProduct: (productId: string) => ['smr-admin', 'campaign', 'resolve', productId] as const,
};

const POLICY_LABELS: Record<(typeof TRANSFER_POLICY_VALUES)[number], string> = {
    open: 'Open',
    same_group_only: 'Same group only',
    forbidden: 'Forbidden',
};

const POLICY_OPTIONS = [
    { value: '', label: 'Unset' },
    ...TRANSFER_POLICY_VALUES.map((value) => ({ value, label: POLICY_LABELS[value] })),
];

const DEFAULT_VALUES: TransferRuleFormValues = {
    transfer_in_allowed: true,
    transfer_out_allowed: true,
    transfer_in_policy: '',
    transfer_out_policy: '',
    forbidden_directions: [],
};

/**
 * Bespoke Transfer Rules tab body. Uses the resolve-then-fetch pattern shared by the campaign
 * rules panels (product->campaign resolver, then the campaign-scoped resource), but drives a
 * react-hook-form + Zod form instead of a config-settings data-table, because the Transfer
 * ruleset is a single structured PUT payload rather than per-row settings.
 */
export function CampaignTransferRulePanel({ productId, product }: Props) {
    const resolveQuery = useQuery({
        queryKey: resolveCampaignKeys.forProduct(productId),
        queryFn: () => resolveProductCampaign(productId),
    });

    const campaignId = resolveQuery.data?.campaign_id;

    const { query: rulesQuery, updateMutation } = useCampaignTransferRules(campaignId);

    const isRetired = Boolean(product.deleted_at);

    // The form itself is NOT wrapped in PermissionGuard, so a user with only
    // campaign.read still sees the current policy (matches the plan's "the read-only
    // view renders without the guard" requirement). Individual controls are disabled
    // for that case; only the Save action (the real mutating affordance) is additionally
    // gated by PermissionGuard below. This is a UX convenience — the real authority is
    // the 4a backend's permission:campaign.edit + audit middleware on the PUT.
    const { hasPermission } = usePermissions();
    const canEdit = hasPermission({ resource: 'campaign', action: 'edit' });
    const controlsDisabled = isRetired || !canEdit;

    const {
        control,
        handleSubmit,
        reset,
        formState: { isDirty, isSubmitting },
    } = useForm<TransferRuleFormValues>({
        resolver: zodResolver(transferRuleSchema),
        defaultValues: DEFAULT_VALUES,
    });

    // Seed from the GET only while the form is clean, so a background refetch never
    // clobbers in-progress edits; the post-save reset below handles the saved state.
    useEffect(() => {
        if (!rulesQuery.data || isDirty) return;
        reset(toTransferRuleFormValues(rulesQuery.data));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rulesQuery.data]);

    const onSubmit = (values: TransferRuleFormValues) => {
        updateMutation.mutate(toTransferRulePayload(values), {
            onSuccess: (saved) => reset(toTransferRuleFormValues(saved)),
        });
    };

    if (resolveQuery.isLoading) {
        return <p className="text-sm text-mp-muted">Loading transfer rules…</p>;
    }

    const noCampaignAlert = (
        <Alert variant="warning">
            <span>This product has no campaign yet. Transfer rules are unavailable until one is provisioned.</span>
        </Alert>
    );

    // Phase 0's product->campaign resolver returns a 404 for an unprovisioned product, so a
    // real "no campaign yet" case surfaces as a 404-shaped ApiError here, not as a 200 with an
    // empty campaign_id. Handle it before the generic error branch so the friendly warning
    // renders instead of the scary "Failed to load" alert. All other resolver errors still
    // fall through to the generic error alert below.
    if (isNotFoundError(resolveQuery.error)) {
        return noCampaignAlert;
    }

    if (resolveQuery.isError) {
        return (
            <Alert variant="error">
                <span>Failed to load this product&apos;s campaign. Transfer rules are unavailable.</span>
            </Alert>
        );
    }

    // Belt-and-braces: also cover a 200-with-empty-campaign_id shape.
    if (!campaignId) {
        return noCampaignAlert;
    }

    if (rulesQuery.isLoading) {
        return <p className="text-sm text-mp-muted">Loading transfer rules…</p>;
    }

    if (rulesQuery.isError) {
        return (
            <Alert variant="error">
                <span>Failed to load transfer rules.</span>
            </Alert>
        );
    }

    return (
        <Form className="space-y-6 max-w-3xl" onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                    <Controller
                        name="transfer_in_allowed"
                        control={control}
                        render={({ field }) => (
                            <Checkbox
                                id="transfer_in_allowed"
                                label="Allow transfer in"
                                ariaLabel="Allow transfer in"
                                checked={field.value}
                                onChange={field.onChange}
                                disabled={controlsDisabled}
                            />
                        )}
                    />
                </div>
                <div>
                    <Controller
                        name="transfer_out_allowed"
                        control={control}
                        render={({ field }) => (
                            <Checkbox
                                id="transfer_out_allowed"
                                label="Allow transfer out"
                                ariaLabel="Allow transfer out"
                                checked={field.value}
                                onChange={field.onChange}
                                disabled={controlsDisabled}
                            />
                        )}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                    <Label htmlFor="transfer_in_policy">Transfer-in policy</Label>
                    <Controller
                        name="transfer_in_policy"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <Select
                                id="transfer_in_policy"
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                options={POLICY_OPTIONS}
                                // The visible <Label htmlFor> does not associate with Select's
                                // combobox div, which derives its accessible name from placeholder.
                                // Give it a distinct name so screen readers announce this control.
                                placeholder="Transfer-in policy"
                                disabled={controlsDisabled}
                                error={!!error}
                                hint={error?.message}
                            />
                        )}
                    />
                </div>
                <div>
                    <Label htmlFor="transfer_out_policy">Transfer-out policy</Label>
                    <Controller
                        name="transfer_out_policy"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <Select
                                id="transfer_out_policy"
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                options={POLICY_OPTIONS}
                                // See transfer-in policy above: Select's accessible name comes
                                // from placeholder, so a distinct name is required here too.
                                placeholder="Transfer-out policy"
                                disabled={controlsDisabled}
                                error={!!error}
                                hint={error?.message}
                            />
                        )}
                    />
                </div>
            </div>

            <div>
                <Label>Forbidden directions</Label>
                <ForbiddenDirectionsEditor control={control} disabled={controlsDisabled} />
            </div>

            {updateMutation.isError && (
                <Alert variant="error">
                    <span>
                        {isApiError(updateMutation.error)
                            ? updateMutation.error.message
                            : 'Failed to save transfer rules.'}
                    </span>
                </Alert>
            )}

            {!isRetired && (
                <PermissionGuard permission={{ resource: 'campaign', action: 'edit' }}>
                    <Button size="sm" disabled={!isDirty || isSubmitting || updateMutation.isPending}>
                        {updateMutation.isPending ? 'Saving…' : 'Save'}
                    </Button>
                </PermissionGuard>
            )}
        </Form>
    );
}
